import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { Badge } from '@/components/ui/badge';
import { ProductHoverCard } from '@/features/shared/product-hover-card';
import { formatNumber } from '@/lib/format';
import type { PurchasePlanning } from '@/types/core/purchase-planning/purchase-planning';
import { SupplierHoverCard } from '../../shared/supplier-hover-card';

export function usePurchasePlanningTableColumns(): ColumnDef<PurchasePlanning>[] {
  const { t, i18n } = useTranslation('purchasePlanning');
  const locale = i18n.language ?? 'fr';

  return useMemo(
    () => [
      {
        id: 'productId',
        accessorFn: (row) => row.product.id,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('product')} />
        ),
        cell: ({ row }) => <ProductHoverCard product={row.original.product} />,
        enableColumnFilter: true,
        enableHiding: false,
        columnPinning: 'left',
        meta: {
          label: t('product'),
          enableColumnReordering: false,
        },
      },
      {
        id: 'supplierId',
        accessorFn: (row) => row.supplier.id,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('supplier')} />
        ),
        cell: ({ row }) => {
          const supplier = row.original.supplier;
          return (
            <SupplierHoverCard
              className='max-w-[160px] truncate'
              supplier={supplier}
            />
          );
        },
        enableColumnFilter: false,
        enableHiding: false,
        meta: {
          label: t('supplierId'),
          enableColumnReordering: false,
        },
      },
      {
        id: 'quantityToOrder',
        accessorKey: 'quantityToOrder',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('quantity to order')}
          />
        ),
        cell: ({ row }) => (
          <Badge variant='secondary'>{row.getValue('quantityToOrder')}</Badge>
        ),
        enableHiding: false,
        meta: {
          label: t('quantity to order'),
          enableColumnReordering: false,
        },
      },
      {
        id: 'totalVolume',
        accessorKey: 'totalVolume',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('total volume')} />
        ),
        cell: ({ row }) => (
          <div>
            {row.getValue('totalVolume')} <span className='text-xs'>m³</span>
          </div>
        ),
        enableHiding: true,
        meta: {
          label: t('total volume'),
        },
      },
      {
        id: 'totalPrice',
        accessorKey: 'totalPrice',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('total price')} />
        ),
        cell: ({ row }) => {
          const price = row.getValue('totalPrice') as number;
          return (
            <Badge variant='outline'>
              {formatNumber(price, {
                locale,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Badge>
          );
        },
        enableHiding: true,
        meta: {
          label: t('total price'),
        },
      },
      {
        id: 'etaYear',
        accessorKey: 'etaYear',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('eta year')} />
        ),
        cell: ({ row }) => <div>{row.getValue('etaYear')}</div>,
        enableHiding: true,
        meta: {
          label: t('eta year'),
        },
      },
      {
        id: 'etaWeek',
        accessorKey: 'etaWeek',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('eta week')} />
        ),
        cell: ({ row }) => <div>{row.getValue('etaWeek')}</div>,
        enableHiding: true,
        meta: {
          label: t('eta week'),
        },
      },
      {
        id: 'currentStock',
        accessorKey: 'currentStock',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('current stock')} />
        ),
        cell: ({ row }) => <div>{row.getValue('currentStock')}</div>,
        enableHiding: true,
        meta: {
          label: t('current stock'),
        },
      },
      {
        id: 'moq',
        accessorKey: 'moq',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('minimum order quantity')}
          />
        ),
        cell: ({ row }) => <div>{row.getValue('moq')}</div>,
        enableHiding: true,
        meta: {
          label: t('minimum order quantity'),
        },
      },
      {
        id: 'batchQuantity',
        accessorKey: 'batchQuantity',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('batch quantity')} />
        ),
        cell: ({ row }) => <div>{row.getValue('batchQuantity')}</div>,
        enableHiding: true,
        meta: {
          label: t('batch quantity'),
        },
      },
      {
        id: 'rotationSegment',
        accessorKey: 'rotationSegment',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('rotation segment')}
          />
        ),
        cell: ({ row }) => <div>{row.getValue('rotationSegment')}</div>,
        enableHiding: true,
        meta: {
          label: t('rotation segment'),
        },
      },
      {
        id: 'averageSalesForecast',
        accessorKey: 'averageSalesForecast',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('average sales forecast')}
          />
        ),
        cell: ({ row }) => <div>{row.getValue('averageSalesForecast')}</div>,
        enableHiding: true,
        meta: {
          label: t('average sales forecast'),
        },
      },
      {
        id: 'minCoverageQuantity',
        accessorKey: 'minCoverageQuantity',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('minimum coverage quantity')}
          />
        ),
        cell: ({ row }) => <div>{row.getValue('minCoverageQuantity')}</div>,
        enableHiding: true,
        meta: {
          label: t('minimum coverage quantity'),
        },
      },
    ],
    [t, locale],
  );
}
